import {useEffect, useState} from "react";
import type {BeatPlanningCalendarEvent, EventColorMap} from "~/components/beatPlanningCalendar/beatPlanningCalendar";

export type YearDayStats = {
    count: number;
    byType: Map<string, {label: string; count: number}>;
    statusLine: string;
};

export type DerivedEventCalendarStats = {
    eventDayKeys: Set<string>;
    monthEventTotals: Map<string, number>;
    monthTypeBars: Map<string, string>;
    yearViewDayStats: Map<string, YearDayStats>;
};

type CalendarStatsAccumulator = {
    eventDayKeysSet: Set<string>;
    monthTotals: Map<string, number>;
    monthTypeCounts: Map<string, Map<string, {count: number; color: string}>>;
    yearStats: Map<string, YearDayStats>;
};

const EMPTY_DERIVED_EVENT_CALENDAR_STATS: DerivedEventCalendarStats = {
    eventDayKeys: new Set<string>(),
    monthEventTotals: new Map<string, number>(),
    monthTypeBars: new Map<string, string>(),
    yearViewDayStats: new Map<string, YearDayStats>(),
};

function toLocalDayKey(date: Date): string {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function toIsoMonthKey(date: Date): string {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}

function scheduleIdleWork(callback: () => void): {cancel: () => void} {
    if (typeof window !== "undefined" && typeof window.requestIdleCallback === "function") {
        const handle = window.requestIdleCallback(() => {
            callback();
        });
        return {
            cancel: () => {
                if (typeof window.cancelIdleCallback === "function") {
                    window.cancelIdleCallback(handle);
                }
            },
        };
    }
    const timeoutHandle = window.setTimeout(() => {
        callback();
    }, 0);
    return {
        cancel: () => {
            window.clearTimeout(timeoutHandle);
        },
    };
}

function finalizeStats(
    accumulator: CalendarStatsAccumulator,
    shouldComputeYearViewStats: boolean,
): DerivedEventCalendarStats {
    if (shouldComputeYearViewStats === false) {
        return {
            eventDayKeys: accumulator.eventDayKeysSet,
            monthEventTotals: new Map<string, number>(),
            monthTypeBars: new Map<string, string>(),
            yearViewDayStats: new Map<string, YearDayStats>(),
        };
    }

    accumulator.yearStats.forEach((entry) => {
        entry.statusLine = Array.from(entry.byType.values())
            .map((item) => `${item.label} (${item.count})`)
            .join(", ");
    });

    const monthBars = new Map<string, string>();
    accumulator.monthTypeCounts.forEach((typeMap, monthKey) => {
        const segments = Array.from(typeMap.values()).sort((a, b) => b.count - a.count);
        const total = segments.reduce((sum, segment) => sum + segment.count, 0);
        if (total <= 0) {
            monthBars.set(monthKey, "var(--ds-surface, #ffffff)");
            return;
        }
        let cursor = 0;
        const gradientStops = segments.map((segment, index) => {
            const start = cursor;
            const chunk = (segment.count / total) * 100;
            cursor += chunk;
            const end = index === segments.length - 1 ? 100 : cursor;
            return `${segment.color} ${start}% ${end}%`;
        });
        monthBars.set(monthKey, `linear-gradient(90deg, ${gradientStops.join(", ")})`);
    });

    return {
        eventDayKeys: accumulator.eventDayKeysSet,
        monthEventTotals: accumulator.monthTotals,
        monthTypeBars: monthBars,
        yearViewDayStats: accumulator.yearStats,
    };
}

function processEvent(
    event: BeatPlanningCalendarEvent & {
        backgroundColor?: string;
        borderColor?: string;
        textColor?: string;
    },
    accumulator: CalendarStatsAccumulator,
    shouldComputeYearViewStats: boolean,
    getEventColor: ((type: string | undefined) => EventColorMap) | undefined,
    getEventTypeLabel: ((type: string) => string) | undefined,
): void {
    const eventStart = new Date(event.start);
    if (Number.isNaN(eventStart.getTime())) {
        return;
    }
    const eventEnd = new Date(event.end);
    const safeEndExclusive =
        Number.isNaN(eventEnd.getTime()) || eventEnd.getTime() <= eventStart.getTime()
            ? new Date(eventStart.getTime() + 1)
            : eventEnd;
    const safeEndIncluded = new Date(safeEndExclusive.getTime() - 1);
    let typeKey = "__none__";
    let statusLabel = "Event";
    let typeColor = "var(--ds-surface, #ffffff)";

    if (shouldComputeYearViewStats === true) {
        const extendedProps = event.extendedProps as {type?: string; typeLabel?: string} | undefined;
        const statusType = extendedProps?.type ?? event.type;
        statusLabel =
            extendedProps?.typeLabel ?? (statusType !== undefined ? getEventTypeLabel?.(statusType) : undefined) ?? "Event";
        typeKey = statusType ?? "__none__";
        const statusColorMap = getEventColor?.(statusType);
        typeColor =
            statusColorMap?.borderColor ??
            statusColorMap?.backgroundColor ??
            event.borderColor ??
            event.backgroundColor ??
            "var(--ds-surface, #ffffff)";
    }

    const dayCursor = new Date(eventStart);
    dayCursor.setHours(0, 0, 0, 0);
    const dayEndBoundary = new Date(safeEndExclusive.getTime() - 1);
    dayEndBoundary.setHours(0, 0, 0, 0);

    while (dayCursor.getTime() <= dayEndBoundary.getTime()) {
        const dayKey = toLocalDayKey(dayCursor);
        accumulator.eventDayKeysSet.add(dayKey);
        if (shouldComputeYearViewStats === true) {
            let entry = accumulator.yearStats.get(dayKey);
            if (entry === undefined) {
                entry = {count: 0, byType: new Map<string, {label: string; count: number}>(), statusLine: ""};
                accumulator.yearStats.set(dayKey, entry);
            }
            entry.count += 1;
            const existingType = entry.byType.get(typeKey);
            if (existingType !== undefined) {
                existingType.count += 1;
            } else {
                entry.byType.set(typeKey, {label: statusLabel, count: 1});
            }
        }
        dayCursor.setDate(dayCursor.getDate() + 1);
    }

    if (shouldComputeYearViewStats === false) {
        return;
    }

    let monthCursor = new Date(eventStart.getFullYear(), eventStart.getMonth(), 1);
    const lastMonth = new Date(safeEndIncluded.getFullYear(), safeEndIncluded.getMonth(), 1);
    while (monthCursor.getTime() <= lastMonth.getTime()) {
        const monthKey = toIsoMonthKey(monthCursor);
        accumulator.monthTotals.set(monthKey, (accumulator.monthTotals.get(monthKey) ?? 0) + 1);
        let typeMap = accumulator.monthTypeCounts.get(monthKey);
        if (typeMap === undefined) {
            typeMap = new Map<string, {count: number; color: string}>();
            accumulator.monthTypeCounts.set(monthKey, typeMap);
        }
        const existing = typeMap.get(typeKey);
        if (existing === undefined) {
            typeMap.set(typeKey, {count: 1, color: typeColor});
        } else {
            existing.count += 1;
        }
        monthCursor = new Date(monthCursor.getFullYear(), monthCursor.getMonth() + 1, 1);
    }
}

export function useCalendarStats({
    eventsWithColor,
    shouldComputeYearViewStats,
    getEventColor,
    getEventTypeLabel,
}: {
    eventsWithColor: Array<
        BeatPlanningCalendarEvent & {
            backgroundColor?: string;
            borderColor?: string;
            textColor?: string;
        }
    >;
    shouldComputeYearViewStats: boolean;
    getEventColor: ((type: string | undefined) => EventColorMap) | undefined;
    getEventTypeLabel: ((type: string) => string) | undefined;
}): {stats: DerivedEventCalendarStats; isComputing: boolean} {
    const [stats, setStats] = useState<DerivedEventCalendarStats>(EMPTY_DERIVED_EVENT_CALENDAR_STATS);
    const [isComputing, setIsComputing] = useState(false);

    useEffect(() => {
        let cancelled = false;
        let currentSchedulerCancel: (() => void) | null = null;
        setIsComputing(true);

        const accumulator: CalendarStatsAccumulator = {
            eventDayKeysSet: new Set<string>(),
            monthTotals: new Map<string, number>(),
            monthTypeCounts: new Map<string, Map<string, {count: number; color: string}>>(),
            yearStats: new Map<string, YearDayStats>(),
        };
        const chunkSize = 150;
        let eventIndex = 0;

        const runNextChunk = () => {
            if (cancelled === true) {
                return;
            }
            const chunkEnd = Math.min(eventIndex + chunkSize, eventsWithColor.length);
            while (eventIndex < chunkEnd) {
                const event = eventsWithColor[eventIndex];
                if (event !== undefined) {
                    processEvent(
                        event,
                        accumulator,
                        shouldComputeYearViewStats,
                        getEventColor,
                        getEventTypeLabel,
                    );
                }
                eventIndex += 1;
            }

            if (eventIndex < eventsWithColor.length) {
                const idleWork = scheduleIdleWork(runNextChunk);
                currentSchedulerCancel = idleWork.cancel;
                return;
            }

            const finalized = finalizeStats(accumulator, shouldComputeYearViewStats);
            if (cancelled === false) {
                setStats(finalized);
                setIsComputing(false);
            }
        };

        const idleWork = scheduleIdleWork(runNextChunk);
        currentSchedulerCancel = idleWork.cancel;

        return () => {
            cancelled = true;
            if (currentSchedulerCancel !== null) {
                currentSchedulerCancel();
            }
        };
    }, [eventsWithColor, shouldComputeYearViewStats, getEventColor, getEventTypeLabel]);

    return {stats: stats, isComputing: isComputing};
}
